/*!
 * scopeViews.js — the ways a live scope can draw its sound
 * © 2026 Rob Canning — GPLv3
 *
 * A box's view (viz(s3, view:polar)) is kept on the box, data-viz-view, and
 * every scope drawn in it reads it each frame (scope.js startScope):
 *
 *   wave         the oscilloscope: level across time (the default)
 *   polar        the same signal wrapped round the box's middle --
 *                angle is time, distance from the middle is level
 *   phase        the signal against itself a moment later (data-viz-lag
 *                samples): x/y figures, a phase portrait
 *   xy           the signal across, another sound (data-viz-y, its uid) up:
 *                Lissajous figures, oscilloscope art
 *   spectrum     level per frequency, low to high on a log scale
 *   ridge        the wave's recent past, a line a moment, stacked and
 *   waterfall    hiding each other -- the spectrum's (scopeRidge.js)
 *   barcode      the spectrum as bars: a column on while its band is above
 *                its own average (scopeBarcode.js)
 *
 * The line views return their points as a flat array [x0, y0, x1, y1 ...]
 * in the scope's own units: drawn as the scope's polyline (pointsAttr), or,
 * with a trail (viz(s3, trail:0.9)), onto a canvas that fades a little
 * every frame instead of being wiped (paintTrail). Each works on one
 * analyser frame:
 * float time data (-1..1) -- byte data has only a handful of levels at a
 * quiet amp, and scaled up to the box every trace came out as stairs --
 * and byte frequency data (0..255 over the analyser's dB range).
 *
 * A frame is scaled to the box by its own loudest sample, so it always
 * touches the same edges -- or, given a `ref` level, by that: a box on
 * Live's stage passes one that follows the sound over a second or two
 * (followLevel), so a figure swells past its box on a loud note and shrinks
 * as it dies, its canvas reaching out round the box (SPILL) to hold it.
 */

const SVG_NS = "http://www.w3.org/2000/svg";
const XHTML_NS = "http://www.w3.org/1999/xhtml";

export const VIEWS = new Set(["wave", "polar", "phase", "xy", "spectrum", "ridge", "waterfall", "barcode"]);
export const FREQUENCY_VIEWS = new Set(["spectrum", "waterfall", "barcode"]);

const LOW_HZ = 20;

/**
 * The widest a drawing canvas gets, in px. Two screens of a 4K display at
 * 2x fit: any less and a wide box's trail is stretched from too few pixels
 * and looks blocky. GPUs take 8192 (most take 16384).
 */
export const CANVAS_MAX = 8192;

/**
 * The biggest swing from the centre line, for scaling to the box; `quiet`
 * for silence (a flat line, not noise blown up).
 */
function peakOf(data, step = 1, quiet = 1) {
  let peak = 0;
  for (let i = 0; i < data.length; i += step) {
    const a = Math.abs(data[i]);
    if (a > peak) peak = a;
  }
  return peak > 1e-4 ? peak : quiet;
}

/**
 * How far a box's canvas reaches past it on each side, as a share of the
 * box's shorter side: room for a figure scaled to followLevel to spill
 * into. A loud note's peak is rarely more than twice the level followed.
 */
export const SPILL = 0.5;

/**
 * The level a box's figure is scaled to: the frame's peak, followed --
 * up over RISE seconds, down over FALL -- and held through silence.
 * Kept on `state` under `key`; `dt` seconds since the last frame.
 */
const RISE = 0.15, FALL = 2;
export function followLevel(state, key, data, dt) {
  const peak = peakOf(data, 1, 0), was = state[key];
  if (!was) return (state[key] = peak || 0);
  if (!peak) return was;
  const tau = peak > was ? RISE : FALL;
  return (state[key] = was + (peak - was) * (1 - Math.exp(-Math.min(dt, 0.25) / tau)));
}

/**
 * A figure that spills, eased in before its canvas's edge: past KNEE of
 * the way out it bends towards a rim just inside the edge (RIM: the line's
 * width kept clear of it), so a loud note swells into a soft rim instead
 * of being cut off square. `round`: the rim is the ellipse inside the
 * spill area (polar, phase, xy); otherwise up and down only (wave).
 */
const KNEE = 0.55, RIM = 0.97;
export function easeIntoSpill(pts, draw, spill, round = true) {
  const m = spill * Math.min(draw.width, draw.height);
  if (!(m > 0) || !pts) return pts;
  const cx = draw.x + draw.width / 2, cy = draw.y + draw.height / 2;
  const rx = draw.width / 2 + m, ry = draw.height / 2 + m;
  const ease = d => d <= KNEE ? 1 : (KNEE + (RIM - KNEE) * Math.tanh((d - KNEE) / (RIM - KNEE))) / d;
  for (let i = 0; i < pts.length; i += 2) {
    const u = (pts[i] - cx) / rx, v = (pts[i + 1] - cy) / ry;
    if (round) {
      const k = ease(Math.hypot(u, v));
      pts[i] = cx + u * k * rx; pts[i + 1] = cy + v * k * ry;
    } else pts[i + 1] = cy + v * ease(Math.abs(v)) * ry;
  }
  return pts;
}

/** The area a box's figure may spill into: `draw` grown by `spill` of its shorter side. */
function spillArea(draw, spill) {
  const m = spill * Math.min(draw.width, draw.height);
  return m > 0 ? { x: draw.x - m, y: draw.y - m, width: draw.width + 2 * m, height: draw.height + 2 * m } : draw;
}

/** A flat point array as a polyline's points attribute. */
export function pointsAttr(a) {
  let s = "";
  for (let i = 0; i < a.length; i += 2) s += `${a[i].toFixed(1)},${a[i + 1].toFixed(1)} `;
  return s;
}

/**
 * A frame's points [x, y, x, y ...] turned by `ang` (radians) round the
 * middle of the drawing box -- viz(s3, spin:), each voice its own way.
 */
export function turnPoints(a, draw, ang) {
  const cx = draw.x + draw.width / 2, cy = draw.y + draw.height / 2;
  const c = Math.cos(ang), s = Math.sin(ang);
  const out = new Array(a.length);
  for (let i = 0; i < a.length; i += 2) {
    const dx = a[i] - cx, dy = a[i + 1] - cy;
    out[i] = cx + dx * c - dy * s;
    out[i + 1] = cy + dx * s + dy * c;
  }
  return out;
}

/** Level across time: the classic scope. */
export function wavePoints(data, draw, vis = null, ref = 0) {
  const n = Math.max(2, Math.min(data.length, Math.round(draw.width)));
  const step = Math.max(1, Math.floor(data.length / n));
  const xStep = draw.width / n;
  const midY = draw.y + draw.height / 2;
  const scale = (draw.height / 2) * 0.8 / (ref || peakOf(data, step));
  const [lo, hi] = vis ? [vis.x - xStep, vis.x + vis.width + xStep] : [-Infinity, Infinity];
  const a = [];
  for (let i = 0, si = 0; i < n && si < data.length; i++, si += step) {
    const x = draw.x + i * xStep;
    if (x >= lo && x <= hi) a.push(x, midY - data[si] * scale);
  }
  return a;
}

/** The frame wrapped once round the middle: radius swings with the level. */
export function polarPoints(data, draw, ref = 0) {
  const n = Math.min(data.length, 720);
  const step = data.length / n;
  const cx = draw.x + draw.width / 2, cy = draw.y + draw.height / 2;
  const rx = draw.width / 2 * 0.5, ry = draw.height / 2 * 0.5;   // the rest line
  const k = 0.9 / (ref || peakOf(data));
  const out = [];
  for (let i = 0; i <= n; i++) {
    const v = data[Math.floor((i % n) * step)] * k;   // -0.9 .. 0.9
    const a = (2 * Math.PI * i) / n - Math.PI / 2;
    out.push(cx + Math.cos(a) * rx * (1 + v), cy + Math.sin(a) * ry * (1 + v));
  }
  return out;
}

/** x now, y `lag` samples later: a figure that turns with the sound. */
export function phasePoints(data, draw, lag = 12, ref = 0) {
  lag = Math.max(1, Math.min(data.length - 2, Math.round(lag)));
  const n = Math.min(data.length - lag, 1024);
  const cx = draw.x + draw.width / 2, cy = draw.y + draw.height / 2;
  const k = 0.9 / (ref || peakOf(data));
  const a = [];
  for (let i = 0; i < n; i++) {
    a.push(cx + data[i] * k * draw.width / 2, cy - data[i + lag] * k * draw.height / 2);
  }
  return a;
}

/**
 * This sound across, another up (both frames from the same moment), each
 * scaled to fill its own axis: two sines at 2:3 are a Lissajous figure
 * across the whole box.
 */
export function xyPoints(dx, dy, draw, refX = 0, refY = 0) {
  const n = Math.min(dx.length, dy.length, 1024);
  const cx = draw.x + draw.width / 2, cy = draw.y + draw.height / 2;
  const kx = 0.9 / (refX || peakOf(dx)), ky = 0.9 / (refY || peakOf(dy));
  const a = [];
  for (let i = 0; i < n; i++) {
    a.push(cx + dx[i] * kx * draw.width / 2, cy - dy[i] * ky * draw.height / 2);
  }
  return a;
}

/**
 * One cycle of the signal, in samples, from where it crosses upward: the
 * span from the first crossing to the last over the crossings between.
 * Smoothed from frame to frame in `state` (a noisy estimate would make the
 * figure shiver); the last good one is kept through silence or noise. A
 * note, detuned or not, gives its own cycle; a chord gives the spacing of
 * its crossings, which is shorter than its true repeat -- cheap, and the
 * figure still holds still while the chord moves together.
 */
export function cyclePeriod(data, state) {
  let mean = 0;
  for (let i = 0; i < data.length; i++) mean += data[i];
  mean /= data.length;
  const thr = 0.1 * peakOf(data);
  let armed = false, first = -1, last = -1, n = 0;
  for (let i = 0; i < data.length; i++) {
    const v = data[i] - mean;
    if (v < -thr) armed = true;
    else if (armed && v > 0) {
      armed = false;
      if (first < 0) first = i;
      last = i; n++;
    }
  }
  if (n >= 2) {
    const p = (last - first) / (n - 1);
    state._period = state._period ? state._period * 0.85 + p * 0.15 : p;
  }
  return state._period || data.length / 4;
}

/**
 * The phase view's lag in samples, from the box's (or shape's) settings:
 * data-viz-cycle, a fraction of the sound's own cycle -- 0.25 keeps a sine
 * a circle at any pitch -- or else data-viz-lag, samples (default 12).
 */
export function phaseLag(ds, data, state) {
  const cycle = Number(ds?.vizCycle);
  if (ds?.vizCycle != null && Number.isFinite(cycle)) return Math.max(1, Math.round(cycle * cyclePeriod(data, state)));
  return Number(ds?.vizLag) || 12;
}

/**
 * The level at a fraction 0..1 across a log frequency axis, 0..1. Low down
 * one bin spans many pixels: reading between bins keeps it a curve, not
 * steps.
 */
export function levelAt(freq, f, sampleRate) {
  const nyq = sampleRate / 2;
  const hz = LOW_HZ * Math.pow(nyq / LOW_HZ, f);
  const b = Math.min(freq.length - 1, (hz / nyq) * freq.length);
  const i = Math.floor(b), t = b - i;
  const a = freq[i], c = freq[Math.min(freq.length - 1, i + 1)];
  return (a + (c - a) * t) / 255;
}

/** Level per frequency, low on the left. `freq` is byte frequency data. */
export function spectrumPoints(freq, draw, sampleRate, vis = null) {
  const n = Math.max(2, Math.min(512, Math.round(draw.width)));
  const bottom = draw.y + draw.height;
  const xStep = draw.width / (n - 1);
  const [lo, hi] = vis ? [vis.x - xStep, vis.x + vis.width + xStep] : [-Infinity, Infinity];
  const a = [];
  for (let i = 0; i < n; i++) {
    const x = draw.x + i * xStep;
    if (x < lo || x > hi) continue;
    a.push(x, bottom - levelAt(freq, i / (n - 1), sampleRate) * draw.height * 0.95);
  }
  return a;
}

/**
 * The part of `area` (in the units of `frame`) that is on the screen,
 * widened to half-screen steps so that it changes only now and then as the
 * score moves -- never more than two screens, so a trail's canvas stays at
 * the screen's own density (CANVAS_MAX); null when none of it is.
 * A box many screens wide (viz(s1, size:[10, 1])) is drawn -- and its trail
 * kept -- only here: the rest would be painted every frame for nobody, and
 * was (four times the raster work at ten screens wide).
 */
export function onScreen(frame, area) {
  const m = frame?.getScreenCTM?.();
  if (!m || !area) return area;
  const inv = m.inverse();
  const W = window.innerWidth, H = window.innerHeight;
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const [sx, sy] of [[0, 0], [W, 0], [0, H], [W, H]]) {
    const p = new DOMPoint(sx, sy).matrixTransform(inv);
    x0 = Math.min(x0, p.x); x1 = Math.max(x1, p.x);
    y0 = Math.min(y0, p.y); y1 = Math.max(y1, p.y);
  }
  const hx = (x1 - x0) / 2, hy = (y1 - y0) / 2;
  if (!(hx > 0 && hy > 0)) return area;
  x0 = Math.floor(x0 / hx) * hx; x1 = Math.ceil(x1 / hx) * hx;
  y0 = Math.floor(y0 / hy) * hy; y1 = Math.ceil(y1 / hy) * hy;
  const ax = Math.max(area.x, x0), ay = Math.max(area.y, y0);
  const bx = Math.min(area.x + area.width, x1), by = Math.min(area.y + area.height, y1);
  if (bx <= ax || by <= ay) return null;
  return { x: ax, y: ay, width: bx - ax, height: by - ay };
}

/** Canvas px per box unit: the box's scale on the screen (its CTM `m`) times the screen's own density. */
export const densityOf = m => Math.max(0.5, (m ? Math.hypot(m.a, m.b) : 1) * (window.devicePixelRatio || 1));

/**
 * A canvas over the draw box, in a foreignObject in the scope's group, made
 * on first use and kept on the handle under `key`. `density` is canvas
 * pixels per scope unit. Resizing a canvas clears it -- only done when the
 * box or the density changes.
 */
function boxCanvas(handle, key, draw, density, parent = handle.group) {
  let rec = handle[key];
  if (!rec) {
    const fo = document.createElementNS(SVG_NS, "foreignObject");
    fo.style.pointerEvents = "none";
    const canvas = document.createElementNS(XHTML_NS, "canvas");
    canvas.style.width = "100%";
    canvas.style.height = "100%";
    canvas.style.display = "block";
    fo.appendChild(canvas);
    parent.appendChild(fo);
    // not inside the group: it must go when the scope does (destroyWaveform)
    if (parent !== handle.group) {
      (handle._detached ||= []).push(fo);
      // on the stage, out of the box's reach: its opacity from the first frame (scope.js stageLook after)
      const o = handle._targetEl?.getAttribute?.("opacity");
      if (o != null) fo.style.opacity = o;
    }
    rec = handle[key] = { fo, canvas, sig: "" };
  }
  const sig = `${draw.x},${draw.y},${draw.width},${draw.height},${density.toFixed(2)}`;
  if (rec.sig !== sig) {
    rec.sig = sig;
    rec.fo.setAttribute("x", draw.x); rec.fo.setAttribute("y", draw.y);
    rec.fo.setAttribute("width", draw.width); rec.fo.setAttribute("height", draw.height);
    const w = Math.min(CANVAS_MAX, Math.max(16, Math.round(draw.width * density)));
    const h = Math.min(CANVAS_MAX, Math.max(16, Math.round(draw.height * density)));
    // Resizing clears it. Moved over at the same scale (the part on the
    // screen moving on: onScreen), what is drawn is carried across -- but
    // not when the box itself has moved (viz(at:), a grid's cell): its old
    // trail, carried, would be left where it was, cut off square
    const old = rec.area, sx = w / draw.width, sy = h / draw.height;
    const box = handle._drawBbox;
    const boxSig = box ? `${box.x},${box.y},${box.width},${box.height}` : "";
    let snap = null;
    if (old && old.box === boxSig && Math.abs(old.sx - sx) < sx * 0.01 && Math.abs(old.sy - sy) < sy * 0.01) {
      snap = document.createElement("canvas");
      snap.width = rec.canvas.width; snap.height = rec.canvas.height;
      snap.getContext("2d").drawImage(rec.canvas, 0, 0);
    }
    rec.canvas.width = w; rec.canvas.height = h;
    if (snap) rec.canvas.getContext("2d").drawImage(snap, (old.x - draw.x) * sx, (old.y - draw.y) * sy);
    rec.area = { x: draw.x, y: draw.y, sx, sy, box: boxSig };
  }
  return rec;
}

/** A console box's ridge canvas (scopeRidge.js), over `area` at `density`. */
export function ridgeCanvas(handle, area, density) {
  return boxCanvas(handle, "_ridgeCv", area, density);
}

const clear = rec => rec?.canvas.getContext("2d").clearRect(0, 0, rec.canvas.width, rec.canvas.height);

/**
 * Show one of the scope's drawing surfaces -- "line", "trail" (in the box),
 * "stageTrail" (on the stage, for a turning box) -- and hide the rest. A
 * trail put away starts clean next time.
 */
export function showSurface(handle, which) {
  if (handle._scopeLine) handle._scopeLine.style.display = which === "line" ? "" : "none";
  for (const [key, name] of [["_trail", "trail"], ["_stageTrail", "stageTrail"]]) {
    if (!handle[key]) continue;
    handle[key].fo.style.display = which === name ? "" : "none";
    if (which !== name) clear(handle[key]);
  }
}

/**
 * The frame a box stands in -- its group on the stage -- when an animation
 * has wrapped it (a rotate, a scale), or null when nothing has. A wrapped
 * box moves under its own trail, so its trail is painted in this frame.
 */
export function stageFrameOf(handle) {
  let n = handle.group?.parentNode, wrapped = false;
  while (n && [...(n.classList || [])].some(c => c.startsWith("oscilla-anim"))) { wrapped = true; n = n.parentNode; }
  return wrapped ? n : null;
}

/**
 * The fade a trail gets each frame, as an SVG filter the canvas draws itself
 * through: alpha * keep, minus a little. `secs` is how long a trace takes
 * to fade: keep is what is left after one frame (at 60 a second) of a fall
 * to 1/255 over that time.
 *
 * The minus is the point. Multiplying alone stalls in 8-bit alpha -- at
 * keep 0.95 a pixel at 9/255 times 0.95 rounds back to 9 -- and a turning
 * box, sweeping a whole circle, left a grey haze over everything it had
 * passed. Just over half a step a frame is enough to take every pixel to
 * zero, and small enough to let long tails be long.
 */
const FADE_FLOOR = 0.6 / 255;
const keepFor = (secs, every = 1) => Math.pow(1 / 255, every / (60 * secs));

/**
 * How many frames apart a trail of `secs` is faded. Each pass through the
 * filter also loses about a step to 8-bit rounding, whatever the slope: a
 * trail faded every frame could not outlast ~2 s. Fading a long one less
 * often (by as much more each time) leaves it its length.
 */
const fadeEvery = secs => Math.max(1, Math.min(8, Math.round(secs * 2)));

function fadeFilterId(secs, every) {
  const keep = keepFor(secs, every);
  const id = `lc-trail-fade-${Math.round(secs * 100)}-${every}`;
  if (document.getElementById(id)) return id;
  let defs = document.getElementById("lc-trail-fades");
  if (!defs) {
    defs = document.createElementNS(SVG_NS, "svg");
    defs.id = "lc-trail-fades";
    defs.setAttribute("width", "0"); defs.setAttribute("height", "0");
    defs.style.position = "absolute";
    document.body.appendChild(defs);
  }
  const f = document.createElementNS(SVG_NS, "filter");
  f.id = id;
  f.setAttribute("color-interpolation-filters", "sRGB");
  const ct = document.createElementNS(SVG_NS, "feComponentTransfer");
  const a = document.createElementNS(SVG_NS, "feFuncA");
  a.setAttribute("type", "linear");
  a.setAttribute("slope", String(keep));
  a.setAttribute("intercept", String(-FADE_FLOOR));
  ct.appendChild(a);
  f.appendChild(ct);
  defs.appendChild(f);
  return id;
}

/**
 * A trail canvas's fade for this frame -- every few frames for a long one
 * (fadeEvery). `state` keeps the frame count. Returns the 2d context.
 */
export function fadeTrail(state, canvas, secs) {
  const g = canvas.getContext("2d");
  const every = fadeEvery(secs);
  state._trailTick = (state._trailTick || 0) + 1;
  if (state._trailTick % every !== 0) return g;     // this frame only draws
  if ("filter" in g) {
    // the canvas through the fade filter, onto itself
    g.save();
    g.globalCompositeOperation = "copy";
    g.filter = `url(#${fadeFilterId(secs, every)})`;
    g.drawImage(canvas, 0, 0);
    g.restore();
  } else {
    // no canvas filters (older Safari): multiply only, faint residue and all
    g.globalCompositeOperation = "destination-out";
    g.fillStyle = `rgba(0,0,0,${1 - keepFor(secs, every)})`;
    g.fillRect(0, 0, canvas.width, canvas.height);
    g.globalCompositeOperation = "source-over";
  }
  return g;
}

/**
 * glyphs: -- the trace drawn in characters, not a line: a word over and over
 * along it (glyphs:"DATA"), or digits drawn anew each frame (glyphs:hex, bin,
 * dec). One every `step` px of the trace on the canvas, upright, `fs` px high.
 */
const GLYPH_SETS = { hex: "0123456789ABCDEF", bin: "01", dec: "0123456789" };
function glyphsAlong(g, pts, X, Y, glyphs, fs) {
  const set = GLYPH_SETS[glyphs];
  const word = set ? null : String(glyphs);
  if (!set && !word) return;
  g.font = `${fs}px 'Iosevka Term', 'IBM Plex Mono', ui-monospace, monospace`;
  g.textAlign = "center";
  g.textBaseline = "middle";
  const step = fs * 0.75;
  let left = 0, n = 0;
  let px = X(pts[0]), py = Y(pts[1]);
  for (let i = 2; i < pts.length; i += 2) {
    const x = X(pts[i]), y = Y(pts[i + 1]);
    let seg = Math.hypot(x - px, y - py);
    // characters on this segment, every step px along the trace
    while (seg > 0 && left <= seg) {
      const t = left / seg;
      const ch = set ? set[Math.floor(Math.random() * set.length)] : word[n % word.length];
      if (ch.trim()) g.fillText(ch, px + (x - px) * t, py + (y - py) * t);
      n++;
      left += step;
    }
    left -= seg;
    px = x; py = y;
  }
}

/**
 * One frame of a trail: what is there fades (over `secs` seconds), the new trace
 * goes on top. Drawn at the screen's own density, so lines stay sharp.
 */
export function paintTrail(handle, pts, draw, secs, color, widthPx, stage = null, spill = 0, glyphs = null) {
  if (stage) { pts = toStage(handle, stage, pts); draw = onScreen(stage, sweepArea(handle, spill)); if (!pts || !draw) return; }
  else draw = spillArea(draw, spill);
  const m = (stage || handle.group)?.getScreenCTM?.();
  const density = densityOf(m);
  const { canvas } = stage
    ? boxCanvas(handle, "_stageTrail", draw, density, stage)
    : boxCanvas(handle, "_trail", draw, density);
  // no trail: a clean canvas each frame -- a console box draws its trace
  // here too, as a big SVG trace is costly to raster every frame (scope.js)
  const g = secs > 0 ? fadeTrail(handle, canvas, secs) : canvas.getContext("2d");
  if (!(secs > 0)) g.clearRect(0, 0, canvas.width, canvas.height);
  if (pts.length < 4) return;
  const sx = canvas.width / draw.width, sy = canvas.height / draw.height;
  if (glyphs) {
    // characters along the trace (width: their size, 8 px a unit of it)
    g.fillStyle = color;
    glyphsAlong(g, pts, x => (x - draw.x) * sx, y => (y - draw.y) * sy, glyphs, Math.max(6, widthPx * 8) * (window.devicePixelRatio || 1));
    return;
  }
  g.strokeStyle = color;
  g.lineWidth = widthPx * (window.devicePixelRatio || 1);
  g.lineJoin = "round";
  g.beginPath();
  g.moveTo((pts[0] - draw.x) * sx, (pts[1] - draw.y) * sy);
  for (let i = 2; i < pts.length; i += 2) g.lineTo((pts[i] - draw.x) * sx, (pts[i + 1] - draw.y) * sy);
  g.stroke();
}


/** The scope's points, carried through its turning wrappers into the stage frame. */
function toStage(handle, stage, pts) {
  const g = handle.group.getScreenCTM(), f = stage.getScreenCTM();
  if (!g || !f) return null;
  const m = f.inverse().multiply(g);
  const out = new Array(pts.length);
  for (let i = 0; i < pts.length; i += 2) {
    out[i] = m.a * pts[i] + m.c * pts[i + 1] + m.e;
    out[i + 1] = m.b * pts[i] + m.d * pts[i + 1] + m.f;
  }
  return out;
}

/**
 * Where a turning box can reach, in the stage frame: the square round its
 * pivot (data-pivot, its middle) that its corners sweep, and the spill
 * round that. Changes when the
 * box is moved or resized, which starts the trail afresh.
 */
function sweepArea(handle, spill = 0) {
  const box = handle._targetEl;
  const [cx, cy] = String(box?.dataset?.pivot || "").split(",").map(Number);
  const w = Number(box?.getAttribute("width")), h = Number(box?.getAttribute("height"));
  if (![cx, cy, w, h].every(Number.isFinite)) return null;
  const r = Math.hypot(w, h) / 2 * 1.02 + spill * Math.min(w, h);
  return { x: cx - r, y: cy - r, width: 2 * r, height: 2 * r };
}
